import React, { FunctionComponent as FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../../../state";
import { State } from "../../../../state";
import "./index.scss";

import Panel from "../../common/Panel";
import MeanBarChart from "../../common/Visualization/MeanBarChart";
import AreaChart from "../../common/Visualization/AreaChart";

import { impactStatisticsViewStyle, themeColor } from "../../../../types/style.global";
import { GlobalStyle } from "../../../../types/style.d";
import { ImpactViewTypes } from "../../../../types/data";
import { ImpactType, ImpactStatisticsEntityType, CitationFlowType } from "../../../../types/data.global";

import { Spin, Row, Checkbox, ConfigProvider, Button, Tooltip } from "antd";
import { CheckOutlined } from '@ant-design/icons';
import type { RadioChangeEvent, CheckboxProps } from 'antd';
import _ from "lodash";
import { QuestionCircleOutlined } from '@ant-design/icons';


interface ImpactStatisticsViewProps {
  viewParam: GlobalStyle.ViewSize;
  panelParam: GlobalStyle.PanelStyle;
  viewName: string;
}

const ImpactStatisticsView: FC<ImpactStatisticsViewProps> = (props) => {
  const { viewParam, panelParam, viewName } = props;

  const dispatch = useDispatch()
  const {
    // backend action creators
    getImpactStatViewData,

    // ui action creators
    cleanMainviewGraph,
    updateImpactStatisticsTypeList,
    setShowEntityTypeLevelGraph,
    updateImpactStatisticsViewLoading,

    // clear all state variables when click confirm button
    updateCitationFlowType,
    updateImpactType,
    setShowEntityFieldLevelGraph,
    // updateIsEntityFieldGraphFinished,
    updateIsInitializeBubbleTreemapFinished,
    updateIsOptimizedBubbleTreemapFinished,
    updateIsEntityTypeGraphFinished,
    clearCheckboxSelection,
    updateSelectedImpactEntityField,
    onClickGrantFieldNode,
    onClickImpactFieldNode,
    updateHighlghtedGrantFieldID,
    isShowLeafNode,
    isShowPredictionFlow,
    isShowHistoricalFlow,
    isShowHistoricalGraph,
    isShowPredictionGraph,

    updateEntityTypeLayoutData,
    updateLeafNodeSizeScale,
    updateLeafNodeInitialPositionList,
    updateBubbleTreemapEntityGroupList,
    updateBubbleTreemapGeometryGroup,
    updateWordlePosition,
    updateWordleEntity,
    isShowWordle,
    updatePredictionSearchResult,
    updatePredictionGrantIDList4PIView,
    updateLeafNodePositionList,
    updateEntityFieldGraph_InputData,
    updateGrantFieldNodePosition,
    updateNodePositionList,
  } = bindActionCreators(actionCreators, dispatch)

  // data reducer
  const grantIDList: string[] = useSelector((state: State) => state.dataReducer?.grantIDList) as string[]
  const numOfPaperData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfPaperData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfHitPaperData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfHitPaperData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfDisruptivePaperData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfDisruptivePaperData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfPatentData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfPatentData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfClinicalTrailData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfClinicalTrailData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfCitingPatentData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfCitingPatentData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfCitingClinicalTrailData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfCitingClinicalTrailData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfCitingPolicyData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfCitingPolicyData) as ImpactViewTypes.AreaChartDataUnit[]
  const numOfCitingNewsfeedData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactStatViewNumOfCitingNewsfeedData) as ImpactViewTypes.AreaChartDataUnit[]

  // ui reducer
  const impactType: string = useSelector((state: State) => state.uiReducer?.impactType) as string
  const isImpactStatisticsViewLoading: boolean = useSelector((state: State) => state.dataReducer?.isImpactStatisticsViewLoading) as boolean
  // 添加这一行：从 Redux store 获取 impactStatisticsTypeList
  const impactStatisticsTypeList: string[] = useSelector((state: State) => state.uiReducer?.impactStatisticsTypeList) as string[]

  // state variables
  const [isDirectImpactDisplay, setDirectImpactDisplay] = useState("none");
  const [isIndirectImpactDisplay, setIndirectImpactDisplay] = useState("block");

  // console.log('isDirectImpactDisplay:', isDirectImpactDisplay)
  // console.log('isIndirectImpactDisplay:', isIndirectImpactDisplay)

  // 保持本地状态，但与 Redux 同步
  const [selectedEntityTypeList, updateSelectedEntityTypeList] = useState<string[]>(impactStatisticsTypeList);


  // console.log('selectedEntityTypeList:', selectedEntityTypeList)
  // console.log("'policy' in selectedEntityTypeList", selectedEntityTypeList.includes('policy'))
  // console.log("'clinical_trial' in selectedEntityTypeList", selectedEntityTypeList.includes('clinical_trial'))
  // console.log("'patent' in selectedEntityTypeList", selectedEntityTypeList.includes('patent'))
  // console.log("'newsfeed' in selectedEntityTypeList", selectedEntityTypeList.includes('newsfeed'))


  // 1. function to updateImpactStatView: call backend to update Impact Statistics View (upper right view)
  /*
    Description:
      Update the Impact Statistics View data.
      Triggered anytime when the grantIDList is updated.
    Input Parameters:
      `selectedGrantIDList`: list[str]
    Output:
      xxx
  */
  const updateImpactStatView = async () => {
    updateImpactStatisticsViewLoading(true)
    try {
      const result = await getImpactStatViewData(grantIDList)
    } catch (error) {
      console.error(error);
    } finally {
      updateImpactStatisticsViewLoading(false)
    }
  }

  // call backend to update Impact Statistics View (upper right view)
  useEffect(() => {
    // if(grantIDList.length > 0){
    //   updateImpactStatView()
    // }
    updateImpactStatView()
  }, [dispatch, grantIDList])

  // update UI (no backend call)
  useEffect(() => {
    if(impactType === ImpactType.DIRECT_IMPACT){
      setDirectImpactDisplay("block");
      setIndirectImpactDisplay("none");
    }
    else{
      setDirectImpactDisplay("none");
      setIndirectImpactDisplay("block");
    }
  }, [impactType])

  // 新增：同步 Redux store 中的值到本地状态
  useEffect(() => {
    updateSelectedEntityTypeList(impactStatisticsTypeList);
  }, [impactStatisticsTypeList]);


  // 2. handle selecting entity type: update store - the selected impact type data, to update main view (center view)
  const onConfirmSelectedEnityTypeClick = () => {
    updateImpactStatisticsTypeList(selectedEntityTypeList);
    setShowEntityTypeLevelGraph(true);

    // ui reducer
    cleanMainviewGraph(false)

        // 1. clear all variables and reset to initial state
    // copy from component <Panel> > `handleChangeImapctTypeDropdown()`
    // same operation: <Panel> > `handleChangeImapctTypeDropdown()`, <QueryView> > `handleConfirmButton()`
    // ===== copy start =====
    // // clear checkbox selection
    // updateImpactStatisticsTypeList([]); // clear all impact type
    updateImpactType(impactType);

    // clear ui reducer: mainview graph
    // setShowEntityTypeLevelGraph(false);
    setShowEntityFieldLevelGraph(false);
    updateIsEntityTypeGraphFinished(false);
    isShowHistoricalGraph(true);
    isShowPredictionGraph(false);
    updateHighlghtedGrantFieldID('all');
    isShowLeafNode(true);
    // updateIsEntityFieldGraphFinished(false);
    updateIsInitializeBubbleTreemapFinished(false);
    updateIsOptimizedBubbleTreemapFinished(false);
    // cleanMainviewGraph(true);
    isShowWordle(false);
    isShowPredictionFlow(false);
    isShowHistoricalFlow(false);
    updateCitationFlowType(CitationFlowType.HISTORICAL);

    // clear data reducer: all data stored in redux about mainview calc should be cleared
    updateEntityTypeLayoutData([]);
    updateLeafNodeSizeScale(0, '');
    // store leaf node positions to state - for drawing edges
    updateLeafNodeInitialPositionList({
      leafNodePositionList: [],
      bubbleTreemapEntityTypeCenterXYList: [],
    });
    updateBubbleTreemapEntityGroupList([]);
    updateBubbleTreemapGeometryGroup([]);
    updateWordlePosition({
      left: 0,
      top: 0,
    });
    updateWordleEntity({
      entity: '',
      fieldID: '',
    });
    updateSelectedImpactEntityField({});
    updatePredictionSearchResult({
      entity: '',
      fieldIDList: [],
    });
    // updatePredictionGrantIDList4PIView([], []);
    // clear up historical selected (1) citation flow (2) highlight node
    onClickGrantFieldNode({
      grantFieldID: '',
      grantFieldName: '',
      impactType: impactType,
    })
    // clear highlighted impact field node
    onClickImpactFieldNode({
      impactType: '',
      impactEntity: '',
      impactFieldID: '',
    })

    // component: OptimizedBubbleTreemap
    updateLeafNodePositionList({
      leafNodePositionList: [],
      bubbleTreemapEntityTypeCenterXYList: [],
    });
    updateEntityFieldGraph_InputData([]);

    // component: GrantTypeNode
    updateGrantFieldNodePosition({
      grantNodePositionList: [],
      isDrawGrantTypeNodeFinished: false,
    })

    // component: EntityFieldLevelCitationFlow
    updateNodePositionList([]);


    // // clear all selection of checkbox
    // clearCheckboxSelection(true)
    // ===== copy end =====
  };


  // 3. event handlers: record selected impact type
  // 3-1. direct impact
  const onScientificImpactPaperChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onScientificImpactPaperChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_PAPER)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_PAPER];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_PAPER)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_PAPER);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onScientificImpactHitPaperChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onScientificImpactHitPaperChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_HIT_PAPER)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_HIT_PAPER];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_HIT_PAPER)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_HIT_PAPER);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onScientificImpactDistruptivePaperChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onScientificImpactDistruptivePaperChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_DISRUPTIVE_PAPER)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_DISRUPTIVE_PAPER];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_DISRUPTIVE_PAPER)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_DISRUPTIVE_PAPER);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onMarketplaceImpactChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onMarketplaceImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_PATENT)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_PATENT];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_PATENT)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_PATENT);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onMedicineImpactChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onMedicineImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CLINICAL_TRIAL)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_CLINICAL_TRIAL];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CLINICAL_TRIAL)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_CLINICAL_TRIAL);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  // 3-2. indirect impact
  const onIndirectMarketplaceImpactChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onIndirectMarketplaceImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_PATENT)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_CITING_PATENT];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_PATENT)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_CITING_PATENT);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onIndirectMedicineImpactChange: CheckboxProps['onChange'] = (e) => {
    console.log(`onIndirectMedicineImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_CLINICAL_TRIAL)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_CITING_CLINICAL_TRIAL];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_CLINICAL_TRIAL)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_CITING_CLINICAL_TRIAL);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onIndirectPolicyImpactChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onIndirectPolicyImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_POLICY)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_CITING_POLICY];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_POLICY)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_CITING_POLICY);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };
  const onIndirectNewsfeedImpactChange: CheckboxProps['onChange'] = (e) => {
    // console.log(`onIndirectNewsfeedImpactChange = ${e.target.checked}`);
    if (e.target.checked) {
      // 检查是否已包含NUM_PAPER，如果没有，则添加
      if (!selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_NEWSFEED)) {
        const updatedList = [...selectedEntityTypeList, ImpactStatisticsEntityType.NUM_CITING_NEWSFEED];
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    } else {
      // 检查是否包含NUM_PAPER，如果包含，则删除
      if (selectedEntityTypeList.includes(ImpactStatisticsEntityType.NUM_CITING_NEWSFEED)) {
        const updatedList = selectedEntityTypeList.filter(type => type !== ImpactStatisticsEntityType.NUM_CITING_NEWSFEED);
        console.log('updatedList:', updatedList)
        // updateImpactStatisticsTypeList(updatedList);
        // setShowEntityTypeLevelGraph(true);
        updateSelectedEntityTypeList(updatedList);
      }
    }
  };


  // 4. visualization
  // const areaChartWidth = impactStatisticsViewStyle.areaChart.width;
  const areaChartHeight = impactStatisticsViewStyle.areaChart.height;
  const areaChartColor = [themeColor.defaultMode.barChartBarColor, themeColor.defaultMode.barChartBarHighlight]

  const areaChartParentDivRef = useRef<HTMLDivElement>(null);
  const [areaChartParentDivSize, setAreaChartParentDivSize] = useState({ width: 0, height: 0 });
  const [areaChartWidth, setAreaChartWidth] = useState(0);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!areaChartParentDivRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const cr = entry.contentRect;
        const newWidth = cr.width;
        const newHeight = cr.height;

        // Only update if the size actually changed significantly (avoid micro-changes)
        const threshold = 1; // 1px threshold
        const currentSize = areaChartParentDivSize;

        if (Math.abs(newWidth - currentSize.width) > threshold ||
            Math.abs(newHeight - currentSize.height) > threshold) {

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            setAreaChartParentDivSize({ width: newWidth, height: newHeight });
            // const minDiameter = Math.min(newWidth, newHeight)
            // setRadarChartDiameter(minDiameter * (0.24/0.35))
            // setDonutChartDiameter(minDiameter)
            // setRadarChartDiameter(minDiameter * 0.24)
            // setDonutChartDiameter(minDiameter * 0.35)
            setAreaChartWidth(Math.max(0, cr.width));
          });
        }
      }, 16); // ~60fps throttling
    };

    // Handle ResizeObserver loop errors
    const ro = new ResizeObserver((entries) => {
      try {
        handleResize(entries);
      } catch (error) {
        // Suppress ResizeObserver loop errors - these are usually benign
        if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
          console.warn('ResizeObserver loop detected, suppressing error');
          return;
        }
        console.error('ResizeObserver error:', error);
      }
    });

    ro.observe(areaChartParentDivRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      ro.disconnect();
    };
  }, [areaChartParentDivSize.width, areaChartParentDivSize.height]);


  // console.log('areaChartWidth:', areaChartWidth)

  const metricNameElement = (metricName: string, metricNameTooltip: string) => <>
    <span style={{marginRight: '4px'}}>{metricName}</span>
    <Tooltip title={metricNameTooltip}>
      <QuestionCircleOutlined style={{fill: 'grey'}}/>
    </Tooltip>
  </>

  return (
    <div className="impact-statistics-view-layout-container"> {/* 添加一个新的外层容器，用于定位 */}
    <Panel
      viewTitle={viewName}
      width={viewParam.width}
      height={viewParam.height}
      margin={panelParam.margin}
      padding={panelParam.padding}
      panelHeaderHeight={panelParam.panelHeaderHeight}
    >
      <ConfigProvider
        theme={{
          token:{
            colorPrimary: '#7F5539',
          },
          components: {
            Checkbox: {

            }
          }
        }}
      >
        <div className="impact-statistics-view" ref={areaChartParentDivRef}>
          <div className="impact-statistics-comfirm"  style={{position: "absolute", right: "4px", bottom: "12px", zIndex: 10}}>
            <Button shape="circle" icon={<CheckOutlined />} size="middle" onClick={onConfirmSelectedEnityTypeClick}></Button>
          </div>
          <Spin tip="Loading" spinning={isImpactStatisticsViewLoading} size='large'>
            <div className="direct-impact-subview" style={{display: isDirectImpactDisplay}}>
              <div className='chart-unit-div-container'>
                  <div className="scientific-impact-paper-title">
                    <Checkbox onChange={onScientificImpactPaperChange}>
                      {metricNameElement('Scientific Impact', 'Metric: num_paper')}
                      {/* Scientific Impact (num_paper) */}
                    </Checkbox>
                  </div>
                  <div className="scientific-impact-paper-chart" style={{width: areaChartWidth, height: areaChartHeight}}>
                    {/* <AreaChart
                      chartTitle="Scientific Impact Num Of Papers"
                      data={numOfPaperData}
                      width={areaChartWidth}
                      height={areaChartHeight}
                      color={areaChartColor}
                      drawLabel={true}
                    /> */}
                    <MeanBarChart
                      chartTitle="Scientific Impact Num Of Papers"
                      data={numOfPaperData}
                      width={areaChartWidth}
                      height={areaChartHeight}
                      color={areaChartColor}
                      drawLabel={true}
                    />
                  </div>
              </div>
              <div className='chart-unit-div-container'>
                <div className="marketplace-impact-title">
                  <Checkbox onChange={onMarketplaceImpactChange}>
                    {metricNameElement('Marketplace Impact', 'Metric: num_patent')}
                    {/* Marketplace Impact (num_patent) */}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Marketplace Impact"
                  data={numOfPatentData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Marketplace Impact"
                  data={numOfPatentData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
              <div className='chart-unit-div-container'>
                <div className="medicine-impact-title">
                  <Checkbox onChange={onMedicineImpactChange}>
                    {metricNameElement('Clinical Impact', 'Metric: num_clinical_trial')}
                    {/* Clinical Impact (num_clinical_trial) */}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Clinical Impact"
                  data={numOfClinicalTrailData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Clinical Impact"
                  data={numOfClinicalTrailData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
              <div className='chart-unit-div-container'>
                <div className="scientific-impact-hit-paper-title">
                  <span className="ant-checkbox-label">
                    {metricNameElement('Scientific Impact (Hit)', 'Metric: num_hit_paper')}
                    {/* Scientific Impact (num_hit_paper) */}
                  </span>
                  {/* <Checkbox onChange={onScientificImpactHitPaperChange} disabled={true}>Scientific Impact(num_hit_paper)</Checkbox> */}
                </div>
                  {/* <AreaChart
                    chartTitle="Scientific Impact Num Of Hit Papers"
                    data={numOfHitPaperData}
                    width={areaChartWidth}
                    height={areaChartHeight}
                    color={areaChartColor}
                    drawLabel={true}
                  /> */}
                  <MeanBarChart
                    chartTitle="Scientific Impact Num Of Hit Papers"
                    data={numOfHitPaperData}
                    width={areaChartWidth}
                    height={areaChartHeight}
                    color={areaChartColor}
                    drawLabel={true}
                  />
              </div>
              <div className='chart-unit-div-container'>
                <div className="scientific-impact-distruptive-paper-title">
                  <span className="ant-checkbox-label">
                    {metricNameElement('Scientific Impact (Disruptive)', 'Metric: num_disruptive_paper')}
                    {/* Scientific Impact (num_disruptive_paper) */}
                  </span>
                  {/* <Checkbox onChange={onScientificImpactDistruptivePaperChange} disabled={true}>Scientific Impact(num_disruptive_paper)</Checkbox> */}
                </div>
                {/* <AreaChart
                  chartTitle="Scientific Impact Num Of Disruptive Papers"
                  data={numOfDisruptivePaperData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Scientific Impact Num Of Disruptive Papers"
                  data={numOfDisruptivePaperData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
            </div>
            <div className="indirect-impact-subview" style={{display: isIndirectImpactDisplay}}>
              <div className='chart-unit-div-container'>
                <div className="indirect-marketplace-impact-title">
                  <Checkbox onChange={onIndirectMarketplaceImpactChange} checked={selectedEntityTypeList.includes('patent')}>
                    {metricNameElement('Marketplace Impact', 'Metric: num_citing_patent')}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Indirect Marketplace Impact"
                  data={numOfCitingPatentData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Indirect Marketplace Impact"
                  data={numOfCitingPatentData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
              <div className='chart-unit-div-container'>
                <div className="indirect-medicine-impact-title">
                  <Checkbox onChange={onIndirectMedicineImpactChange} checked={selectedEntityTypeList.includes('clinical_trial')}>
                    {metricNameElement('Clinical Impact', 'Metric: num_citing_clinical_trial')}
                    {/* Clinical Impact (num_citing_clinical_trial) */}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Clinical Impact"
                  data={numOfCitingClinicalTrailData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Indirect Clinical Impact"
                  data={numOfCitingClinicalTrailData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
              <div className='chart-unit-div-container'>
                <div className="indirect-policy-impact-title">
                  <Checkbox onChange={onIndirectPolicyImpactChange} checked={selectedEntityTypeList.includes('policy')}>
                    {metricNameElement('Policy Impact', 'Metric: num_citing_policy')}
                    {/* Policy Impact (num_citing_policy) */}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Policy Impact"
                  data={numOfCitingPolicyData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Policy Impact"
                  data={numOfCitingPolicyData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
              <div className='chart-unit-div-container'>
                <div className="indirect-newsfeed-impact-title">
                  <Checkbox onChange={onIndirectNewsfeedImpactChange} checked={selectedEntityTypeList.includes('newsfeed')}>
                    {metricNameElement('Media Impact', 'Metric: num_citing_newsfeed')}
                    {/* Media Impact (num_citing_newsfeed) */}
                  </Checkbox>
                </div>
                {/* <AreaChart
                  chartTitle="Newsfeed Impact"
                  data={numOfCitingNewsfeedData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                /> */}
                <MeanBarChart
                  chartTitle="Newsfeed Impact"
                  data={numOfCitingNewsfeedData}
                  width={areaChartWidth}
                  height={areaChartHeight}
                  color={areaChartColor}
                  drawLabel={true}
                />
              </div>
            </div>
          </Spin>
        </div>
      </ConfigProvider>
    </Panel>
    </div>
  );
};

export default ImpactStatisticsView;
